import { useTranslation } from 'react-i18next';
import { MdOutlineBeenhere } from 'react-icons/md';
import Webcam from 'react-webcam';
import { usePermission } from '@kiper/hooks';
import { useFacialRegistrationContext } from '../../../../../../store/FacialRegistration';
import StepLayout from './StepLayout';
import useWebcamCapture from './hooks/useWebcamCapture';
import { Button } from './common';
import CameraErrorAlert, { CameraBlockedAlert } from './CameraErrorAlert';
import * as S from './styles';

const webcamStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const StepCapture = () => {
  const [t] = useTranslation('personalized-invite');
  const {
    capturePhoto,
    primaryColor,
    previousStep,
    goToInvitePage,
  } = useFacialRegistrationContext();

  const permissionsCamera = usePermission('camera');
  const { PermissionStateEnum, permissionState } = permissionsCamera;
  const isCamBlocked = permissionState === PermissionStateEnum.BLOCKED;

  const {
    webcamRef,
    videoConstraints,
    userMediaError,
    handleUserMediaError,
    handleTryAgain,
  } = useWebcamCapture();

  const handleCapture = () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      capturePhoto(imageSrc);
    }
  };

  if (isCamBlocked) {
    return <CameraBlockedAlert onClose={goToInvitePage} />;
  }

  if (userMediaError) {
    return (
      <CameraErrorAlert onTryAgain={handleTryAgain} onClose={goToInvitePage} />
    );
  }

  const footer = (
    <S.WrapperFooter>
      <S.InfoRow>
        <MdOutlineBeenhere size={20} />
        <S.InfoText>{t('facial-registration.capture.privacy-text')}</S.InfoText>
      </S.InfoRow>

      <Button onClick={handleCapture} color={primaryColor}>
        {t('facial-registration.capture.take-photo-button')}
      </Button>
    </S.WrapperFooter>
  );

  return (
    <StepLayout footer={footer} onBack={previousStep}>
      <S.StepTitle>{t('facial-registration.capture.title')}</S.StepTitle>
      <S.StepDescription>
        {t('facial-registration.capture.description')}
      </S.StepDescription>

      <S.CaptureArea>
        <S.CaptureOverlay>
          <Webcam
            ref={webcamRef}
            audio={false}
            screenshotFormat="image/jpeg"
            videoConstraints={videoConstraints}
            onUserMediaError={handleUserMediaError}
            style={webcamStyle}
          />
        </S.CaptureOverlay>
      </S.CaptureArea>
    </StepLayout>
  );
};

export default StepCapture;
